import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import AdminCityListingTitle from "../../../components/AdminLayout/AdminCityListingTitle"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"
import { ICONS } from "../../../constants/icons"
import { get, patch } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useCategories from "../../../hooks/useCategories"
import {
  CITY_CALL_LIST_WITH,
  CITY_CALL_STATUS_TABS,
  CITY_CALL_TAB_TO_API_STATUS,
  CITY_CALL_TYPE_OPTIONS,
  DEFAULT_CITY_CALL_TYPE,
  mapCityCallRow,
  mapCityCallStatusTabs,
} from "./adminCityCallsData"

const PAGE_SIZE = 8
const TOAST_OPTIONS = { transition: Bounce }

const categorySlug = category =>
  String(category || "")
    .toLowerCase()
    .replace(/\s+/g, "-")

/**
 * Admin City Calls for Artists — Figma Manage City - Calls Listing.
 * GET /api/v1/admin/calls?city_ids[]=
 * PATCH /api/v1/admin/calls/update/:id — approve / reject
 */
const AdminCityCalls = () => {
  const { cityId } = useParams()
  const navigate = useNavigate()

  const { categories } = useCategories()

  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState(DEFAULT_CITY_CALL_TYPE)
  const [categoryId, setCategoryId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const [rows, setRows] = useState([])
  const [tabItems, setTabItems] = useState(CITY_CALL_STATUS_TABS)
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.title = "Calls for Artists | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, categoryId, cityId, typeFilter])

  const fetchSummary = useCallback(async () => {
    if (!cityId) {
      setTabItems(CITY_CALL_STATUS_TABS)
      return
    }
    try {
      const params = { "city_ids[]": cityId }
      if (typeFilter) params.type = typeFilter
      const response = await get(endpoints.getAdminCallsSummary, { params })
      if (response?.data?.success) {
        setTabItems(mapCityCallStatusTabs(response.data.result || {}))
      }
    } catch {
      // Keep previous tab counts; list fetch surfaces errors.
    }
  }, [cityId, typeFilter])

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        "with[]": CITY_CALL_LIST_WITH,
        sort_by: "created_at",
        sort_direction: "desc",
      }

      if (includePage) {
        params.page = currentPage
        params.page_size = pageSize
      }

      if (debouncedSearch) params.search = debouncedSearch
      if (cityId) params["city_ids[]"] = cityId
      if (typeFilter) params.type = typeFilter
      if (categoryId) params.category_id = categoryId

      const apiStatus = CITY_CALL_TAB_TO_API_STATUS[activeTab]
      if (apiStatus) params.status = apiStatus

      return params
    },
    [activeTab, categoryId, cityId, currentPage, pageSize, debouncedSearch, typeFilter],
  )

  const fetchCalls = useCallback(async () => {
    if (!cityId) {
      setRows([])
      setTotalItems(0)
      setIsLoading(false)
      setError("Missing city id.")
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminCalls, {
        params: buildListParams(),
      })

      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapCityCallRow)
        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load calls")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load calls")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, cityId, currentPage])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    fetchCalls()
  }, [fetchCalls])

  const updateStatus = async (row, nextUiStatus) => {
    if (!row?.id || isUpdating) return
    const apiStatus = CITY_CALL_TAB_TO_API_STATUS[nextUiStatus]
    if (!apiStatus) return

    setIsUpdating(true)
    try {
      const response = await patch(endpoints.updateAdminCall(row.id), {
        status: apiStatus,
      })
      if (response?.data?.success) {
        toast.success(
          nextUiStatus === "approved" ? "Call approved." : "Call rejected.",
          TOAST_OPTIONS,
        )
        fetchSummary()
        fetchCalls()
      } else {
        toastApiError(response, "Failed to update call")
      }
    } catch (err) {
      toastApiError(err, "Failed to update call")
    } finally {
      setIsUpdating(false)
    }
  }

  const handleExport = async () => {
    if (isExporting || !cityId) return
    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminCalls, {
        params: buildListParams({ includePage: false }),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export calls")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export calls"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, TOAST_OPTIONS)
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `city-calls-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Calls exported.", TOAST_OPTIONS)
    } catch (err) {
      toastApiError(err, "Failed to export calls")
    } finally {
      setIsExporting(false)
    }
  }

  const handleView = row => {
    if (!row?.id) return
    navigate(`/admin/inbox/${row.id}`)
  }

  const skeletonCards = useMemo(
    () => Array.from({ length: 8 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  const itemLabel = activeTab === "awaiting" ? "pending calls" : "calls"

  const filterConfig = useMemo(
    () => [
      {
        key: "type",
        type: "select",
        label: "Type",
        multiple: false,
        options: CITY_CALL_TYPE_OPTIONS,
        value: typeFilter,
        onChange: next => {
          setTypeFilter(Array.isArray(next) ? next[0] || "" : next || "")
        },
      },
      {
        key: "category_id",
        type: "select",
        label: "Category",
        multiple: false,
        searchable: true,
        options: categories,
        value: categoryId,
        onChange: next => {
          setCategoryId(Array.isArray(next) ? next[0] || "" : next || "")
        },
      },
    ],
    [categories, categoryId, typeFilter],
  )

  return (
    <div className="admin-page admin-city-calls">
      <div className="admin-city-calls__toolbar">
        <AdminCityListingTitle label="Calls for Artists" cityId={cityId} />
        <div className="admin-city-calls__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by Title..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-city-calls__search"
          />
          <FilterDropdown label="Filter" filters={filterConfig} />
          <ExportLogButton
            entityLabel="calls"
            onClick={handleExport}
            loading={isExporting}
            disabled={!cityId}
          />
        </div>
      </div>

      <div className="admin-city-calls__tabs-row">
        <ListingTabs
          items={tabItems}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div
        className="admin-city-calls__grid"
        aria-busy={isLoading || undefined}
      >
        {isLoading
          ? skeletonCards.map(key => (
              <article
                key={key}
                className="admin-city-calls__card admin-city-calls__card--skeleton"
              >
                <div className="admin-city-calls__skeleton admin-city-calls__skeleton--media" />
                <div className="admin-city-calls__body">
                  <div className="admin-city-calls__skeleton admin-city-calls__skeleton--line" />
                  <div className="admin-city-calls__skeleton admin-city-calls__skeleton--title" />
                  <div className="admin-city-calls__skeleton admin-city-calls__skeleton--desc" />
                </div>
              </article>
            ))
          : rows.map(row => (
              <article key={row.id} className="admin-city-calls__card">
                <div className="admin-city-calls__media">
                  {row.image ? (
                    <img
                      src={row.image}
                      alt=""
                      className="admin-city-calls__img"
                    />
                  ) : (
                    <div className="admin-city-calls__media-empty" />
                  )}
                  <span
                    className={`admin-city-calls__badge admin-city-calls__badge--${categorySlug(
                      row.category,
                    )}`}
                  >
                    {row.category}
                  </span>
                </div>

                <div className="admin-city-calls__body">
                  <div className="admin-city-calls__copy">
                    <TruncatedText as="p" className="admin-city-calls__org">
                      {row.org}
                    </TruncatedText>
                    <TruncatedText
                      as="h2"
                      className="admin-city-calls__card-title"
                    >
                      {row.title}
                    </TruncatedText>
                    <TruncatedText as="p" className="admin-city-calls__desc">
                      {row.description}
                    </TruncatedText>
                  </div>

                  <div className="admin-city-calls__location">
                    <img
                      src={ICONS.callLocationGreen}
                      alt=""
                      width={14}
                      height={14}
                    />
                    <span>{row.city}</span>
                  </div>

                  <div className="admin-city-calls__meta">
                    <div className="admin-city-calls__poster">
                      <span className="admin-city-calls__posted-label">
                        Posted by:
                      </span>
                      <Avatar
                        src={row.poster.avatar}
                        name={row.poster.name}
                        size="xs"
                      />
                      <TruncatedText className="admin-city-calls__poster-name">
                        {row.poster.name}
                      </TruncatedText>
                    </div>
                    <div className="admin-city-calls__applicants">
                      {row.applicants.map((src, index) => (
                        <Avatar
                          key={`${row.id}-a-${index}`}
                          src={src}
                          name={`Applicant ${index + 1}`}
                          size="xs"
                          className="admin-city-calls__applicant"
                        />
                      ))}
                      {row.applicantExtra > 0 ? (
                        <span className="admin-city-calls__applicant-extra">
                          +{row.applicantExtra}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {row.status === "awaiting" ? (
                    <div className="admin-city-calls__actions">
                      <CustomButton
                        variant="transparent"
                        size="sm"
                        type="button"
                        className="admin-city-calls__approve-btn"
                        disabled={isUpdating}
                        onClick={() => updateStatus(row, "approved")}
                      >
                        Approve
                      </CustomButton>
                      <CustomButton
                        variant="transparent"
                        size="sm"
                        type="button"
                        className="admin-city-calls__reject-btn"
                        disabled={isUpdating}
                        onClick={() => updateStatus(row, "rejected")}
                      >
                        Reject
                      </CustomButton>
                    </div>
                  ) : (
                    <div className="admin-city-calls__actions">
                      <CustomButton
                        variant="transparent"
                        size="sm"
                        type="button"
                        className="admin-city-calls__view-btn"
                        trailingIconSrc={ICONS.arrowRight12}
                        trailingIconAlt=""
                        onClick={() => handleView(row)}
                      >
                        View Detail
                      </CustomButton>
                    </div>
                  )}
                </div>
              </article>
            ))}
      </div>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title={error ? "Couldn’t load calls" : "No calls found"}
          description={error || "Try adjusting your search or filters."}
          actionText={error ? "Try again" : undefined}
          onActionClick={error ? fetchCalls : undefined}
        />
      ) : null}

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel={itemLabel}
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-city-calls__pagination"
      />
    </div>
  )
}

export default AdminCityCalls
